import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OpsShop as OpsShopDetail, ProductLine } from '@popcart/core/v1';
import { api, errorMessage, errorStatus, ApiRequestError } from '../../api';
import { ConfirmDialog, ErrorState, LineDot, Loading, StatusPill, useTitle } from '../../ui/bits';
import { Fig, PageHead, RowMenu, Stat } from '../../ui/viz';
import { Meter, StDot, dotTone, menuItems } from '../../catalog/kit';
import '../../catalog/catalog.css';
import { useToast } from '../../ui/toast';
import { Icon } from '../../ui/Icon';
import { count, initials, minutes, peso } from '../../lib/format.js';
import { opsShopPath, shopPath } from '../../lib/roles.js';
import { ShopRefGate, shopRefKey } from '../../lib/shopRef';
import { opsKeys, useOpsSettings, useOpsShops, type OpsShop } from '../queries';
import { shopStatusView } from '../parts';
import { ShopForm } from './ShopForm';
import {
  DEFAULT_BPS,
  UNVERIFIED_LIVE,
  bpsLabel,
  bpsToPercentInput,
  draftFromShop,
  emailOk,
  emptyShopDraft,
  formFieldFor,
  latLngLabel,
  liveBlock,
  needsVerification,
  normalizeEmail,
  shopKeys,
  shopPatch,
  statusActions,
  statusView,
  validateShopDraft,
  verifyHint,
  verifyReadiness,
  type DraftField,
  type ShopDraft,
} from './lib.js';
import './shops.css';

type Target = 'live' | 'paused' | 'suspended';
type Errors = Partial<Record<DraftField, string>>;

/** The status change's sentence when the server says no: a 409 is the unverified-partner rule. */
function statusProblem(err: unknown) {
  return errorStatus(err) === 409 ? errorMessage(err, UNVERIFIED_LIVE) : errorMessage(err, "Couldn't change the shop. Try again.");
}

function NeedsVerification() {
  return <StatusPill tone="warning">Needs verification</StatusPill>;
}

// ── Shops list ──────────────────────────────────────────────────────────────────────────────

const LINE_WORD: Record<string, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' };

function rowTone(s: OpsShop) {
  return dotTone(shopStatusView(s).tone);
}

export function ShopsPage() {
  useTitle('Shops');
  const shops = useOpsShops();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState<OpsShop | null>(null);

  const setStatus = useMutation({
    mutationFn: (v: { shop: OpsShop; status: Target }) => api.setShopStatus(v.shop.id, v.status),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: opsKeys.shops });
      const prev = qc.getQueryData<OpsShop[]>(opsKeys.shops);
      qc.setQueryData<OpsShop[]>(opsKeys.shops, (xs) => xs?.map((s) => (s.id === v.shop.id ? { ...s, status: v.status } : s)));
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(opsKeys.shops, ctx.prev);
      toast(statusProblem(err), 'error');
    },
    onSuccess: (_d, v) => {
      const verb = v.status === 'live' ? (v.shop.status === 'pending' ? 'is live' : 'is back on') : v.status === 'paused' ? 'is paused' : 'is suspended';
      toast(`${v.shop.name} ${verb}.`);
      setConfirm(null);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['ops'] }),
  });

  const busyFor = (s: OpsShop) => setStatus.isPending && setStatus.variables?.shop.id === s.id;
  const act = (s: OpsShop, status: Target) => setStatus.mutate({ shop: s, status });
  const detail = (s: OpsShop) => opsShopPath(s.slug, s.id);

  /** One visible next step per row; everything else sits in the row menu. */
  const actions = (s: OpsShop) => {
    const busy = busyFor(s);
    const blocked = liveBlock(s);
    const b = (label: string, status: Target, aria: string) => (
      <button type="button" className="btn" disabled={busy} aria-label={aria} onClick={() => act(s, status)}>
        {label}
      </button>
    );
    const main =
      s.status !== 'live' && blocked ? (
        <Link className="btn btn-secondary" to={detail(s)} aria-label={`Review and verify ${s.name}`}>
          Review
        </Link>
      ) : s.status === 'pending' ? (
        b('Go live', 'live', `Put ${s.name} live`)
      ) : s.status === 'paused' ? (
        b('Resume', 'live', `Resume ${s.name}`)
      ) : s.status === 'suspended' ? (
        b('Reinstate', 'live', `Reinstate ${s.name}`)
      ) : (
        <Link className="btn btn-quiet" to={shopPath(s.slug, s.id)} aria-label={`Open ${s.name} in Partner Hub`}>
          Open
        </Link>
      );
    const items = menuItems([
      { label: 'Shop record', icon: 'list', onSelect: () => navigate(detail(s)) },
      s.status !== 'pending' && s.status !== 'live' ? { label: 'Open in Partner Hub', icon: 'swap', onSelect: () => navigate(shopPath(s.slug, s.id)) } : null,
      s.status === 'live' ? { label: 'Pause', icon: 'clock', disabled: busy, onSelect: () => act(s, 'paused') } : null,
      s.status !== 'suspended' ? { label: 'Suspend', icon: 'alert', tone: 'danger', disabled: busy, onSelect: () => setConfirm(s) } : null,
    ]);
    return (
      <div className="actions">
        {main}
        <RowMenu label={`More for ${s.name}`} items={items} />
      </div>
    );
  };

  const all = shops.data ?? [];
  const pending = all.filter((s) => s.status === 'pending');
  const others = all.filter((s) => s.status !== 'pending');
  const unverified = all.filter((s) => needsVerification(s)).length;
  const live = others.filter((s) => s.status === 'live').length;
  const orders = others.reduce((n, s) => n + (s.ordersToday ?? 0), 0);
  const sales = others.reduce((n, s) => n + (s.salesTodayCentavos ?? 0), 0);
  const rated = all.filter((s) => typeof s.rating === 'number');
  const avgRating = rated.length ? rated.reduce((n, s) => n + (s.rating ?? 0), 0) / rated.length : null;

  return (
    <>
      <PageHead
        title="Shops"
        meta={shops.data ? `${live} live · ${pending.length} not live yet${unverified ? ` · ${unverified} to verify` : ''}` : undefined}
        actions={
          <Link className="btn btn-primary" to="/ops/shops/new">
            <Icon name="plus" />
            Add shop
          </Link>
        }
      />
      {shops.isError ? (
        <section className="card">
          <ErrorState error={shops.error} onRetry={() => void shops.refetch()} />
        </section>
      ) : !shops.data ? (
        <Loading label="Loading shops" rows={2} />
      ) : (
        <>
          <div className="dv-stats dv-rise" style={{ ['--i' as string]: 0 }}>
            <Stat label="Live" value={`${live} / ${all.length}`} visual={<ShopDots shops={all} />} />
            <Stat label="Orders today" value={count(orders)} />
            <Stat label="Sales today" value={peso(sales)} sub={avgRating === null ? undefined : `★ ${avgRating.toFixed(1)} avg rating`} />
            <Stat label="To verify" value={count(unverified)} tone={unverified ? 'alert' : undefined} sub={unverified ? 'Partners waiting' : 'All clear'} />
          </div>
          {pending.length ? (
            <section className="card dv-card dv-rise" aria-labelledby="apply-h" style={{ ['--i' as string]: 1 }}>
              <div className="dv-card-h">
                <h2 id="apply-h" title="Customers can’t see these yet. Verify the partner’s papers, then put it live.">
                  Not live yet
                </h2>
                <span className="dv-chip">{pending.length}</span>
              </div>
              <ShopRows shops={pending} actions={actions} detail={detail} />
            </section>
          ) : null}
          <section className="card dv-card dv-rise" aria-labelledby="all-shops-h" style={{ ['--i' as string]: 2 }}>
            <div className="dv-card-h">
              <h2 id="all-shops-h">All shops</h2>
              <span className="dv-chip">{others.length}</span>
            </div>
            <ShopRows shops={others} actions={actions} detail={detail} />
          </section>
        </>
      )}
      <ConfirmDialog
        open={confirm !== null}
        title={`Suspend ${confirm?.name ?? 'this shop'}?`}
        body="Customers stop seeing it right away and open orders stay with the shop. You can reinstate it later."
        confirmLabel="Suspend shop"
        busy={setStatus.isPending}
        onConfirm={() => confirm && act(confirm, 'suspended')}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

/** A dot per shop, coloured by its state: the fleet at a glance. */
function ShopDots({ shops }: { shops: OpsShop[] }) {
  return (
    <span className="mk-fleet" aria-hidden="true">
      {shops.slice(0, 24).map((s) => (
        <i key={s.id} className={`mk-dot lg ${rowTone(s)}`} />
      ))}
    </span>
  );
}

function ShopRows({ shops, actions, detail }: { shops: OpsShop[]; actions(s: OpsShop): ReactNode; detail(s: OpsShop): string }) {
  if (!shops.length) return <div className="dv-quiet">No shops here yet</div>;
  const top = Math.max(1, ...shops.map((s) => s.salesTodayCentavos ?? 0));
  return (
    <div className="table-wrap mk-wrap">
      <table className="mk-table">
        <thead>
          <tr>
            <th scope="col">Shop</th>
            <th scope="col">Sells</th>
            <th scope="col" className="num">
              Orders
            </th>
            <th scope="col" className="num">
              Sales
            </th>
            <th scope="col" className="num">
              Prep
            </th>
            <th scope="col" className="num">
              Rating
            </th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {shops.map((s) => {
            const st = shopStatusView(s);
            const pending = s.status === 'pending';
            const dist = s.distanceKm !== null && s.distanceKm !== undefined ? ` · ${s.distanceKm} km` : '';
            const sells = s.lines.map((l) => LINE_WORD[l] ?? l).join(', ');
            return (
              <tr key={s.id}>
                <th scope="row" className="mk-who">
                  <span className="mk-who-in">
                    <i className={`mk-dot lg ${dotTone(st.tone)}`} aria-hidden="true" title={st.label} />
                    <span className="stack s4" style={{ minWidth: 0 }}>
                      <span className="row wrap mk-name-row">
                        <Link className="mk-name" to={detail(s)} aria-label={`${s.name}: shop record`}>
                          {s.name}
                        </Link>
                        {needsVerification(s) ? <NeedsVerification /> : null}
                      </span>
                      <span className="mk-sub">
                        {st.label} · {s.kind === 'first_party' ? 'First-party' : 'Partner'}
                        {dist}
                      </span>
                    </span>
                  </span>
                </th>
                <td>
                  <span className="mk-lines" role="img" aria-label={sells} title={sells}>
                    {s.lines.map((l: ProductLine) => (
                      <LineDot key={l} line={l} />
                    ))}
                  </span>
                </td>
                <td className="num">{pending ? <span className="muted">—</span> : <Fig text={count(s.ordersToday)} size="xs" animate={false} />}</td>
                <td className="num mk-sales">
                  {pending ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className="mk-figbar">
                      <Fig text={peso(s.salesTodayCentavos)} size="xs" animate={false} />
                      <Meter value={(s.salesTodayCentavos ?? 0) / top} />
                    </span>
                  )}
                </td>
                <td className="num">{pending ? <span className="muted">—</span> : <span className="mk-numtxt">{minutes(s.prepMinutes)}</span>}</td>
                <td className="num">
                  {s.rating === null || s.rating === undefined ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className="mk-rate">
                      <Icon name="star" size="sm" />
                      {s.rating.toFixed(1)}
                    </span>
                  )}
                </td>
                <td className="num">{actions(s)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── the form's shared state ─────────────────────────────────────────────────────────────────

function useDraft(initial: ShopDraft) {
  const [draft, setDraft] = useState<ShopDraft>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const set = <K extends keyof ShopDraft>(k: K, v: ShopDraft[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    const field = (k === 'commissionPct' ? 'commissionPct' : k) as DraftField;
    setErrors((e) => {
      if (!e[field]) return e;
      const { [field]: _drop, ...rest } = e;
      return rest;
    });
  };
  return { draft, setDraft, errors, setErrors, set };
}

/** A server 400 with a field lands under that field; anything else is one line at the bottom. */
function serverErrors(err: unknown, fallback: string): { field: Errors; form: string | null } {
  const msg = errorMessage(err, fallback);
  const f = err instanceof ApiRequestError ? formFieldFor(err.field) : null;
  return f ? { field: { [f]: msg }, form: null } : { field: {}, form: msg };
}

function focusFirstError(form: HTMLFormElement | null) {
  window.setTimeout(() => form?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
}

// ── Add shop ────────────────────────────────────────────────────────────────────────────────

export function OpsShopNewPage() {
  useTitle('Add shop');
  const settings = useOpsSettings();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const { draft, setDraft, errors, setErrors, set } = useDraft(emptyShopDraft(DEFAULT_BPS));
  const [formError, setFormError] = useState<string | null>(null);
  const touched = useRef(false);

  // The default commission comes from Settings (marketplace.defaultCommissionBps), until edited.
  const defaultBps = Number((settings.data as Record<string, Record<string, unknown> | undefined> | undefined)?.marketplace?.defaultCommissionBps);
  const partnerBps = Number.isFinite(defaultBps) ? defaultBps : DEFAULT_BPS;
  useEffect(() => {
    if (!touched.current && draft.kind === 'partner') setDraft((d) => ({ ...d, commissionPct: bpsToPercentInput(partnerBps) }));
  }, [partnerBps]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField: typeof set = (k, v) => {
    if (k === 'commissionPct') touched.current = true;
    if (k === 'kind' && !touched.current) setDraft((d) => ({ ...d, commissionPct: v === 'first_party' ? '0' : bpsToPercentInput(partnerBps) }));
    set(k, v);
  };

  const create = useMutation({
    mutationFn: (body: Parameters<typeof api.createShop>[0]) => api.createShop(body),
    onSuccess: (shop, body) => {
      qc.setQueryData(shopKeys.one(shop.id), shop);
      void qc.invalidateQueries({ queryKey: opsKeys.shops });
      const invited = body.ownerEmail ? ` We emailed ${body.ownerEmail} a link to the Partner Hub.` : '';
      toast(`${shop.name} added. Customers can't see it until it goes live.${invited}`);
      navigate(opsShopPath(shop.slug, shop.id));
    },
    onError: (err) => {
      const s = serverErrors(err, "Couldn't add the shop. Try again.");
      setErrors(s.field);
      setFormError(s.form);
      if (!s.form) focusFirstError(formRef.current);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const r = validateShopDraft(draft, { mode: 'create' });
    if (!r.ok) {
      setErrors(r.errors);
      focusFirstError(formRef.current);
      return;
    }
    setErrors({});
    create.mutate(r.value);
  };

  return (
    <form ref={formRef} className="stack s24" onSubmit={submit} noValidate aria-label="Add shop">
      <header className="dv-head">
        <BackBtn to="/ops/shops" label="Back to shops" />
        <div className="stack s4 grow">
          <h1>Add shop</h1>
          <span className="dv-live">Invite-only · vet the seller first</span>
        </div>
      </header>
      <ShopForm draft={draft} errors={errors} set={setField} mode="create" />
      <div className="card panel sh-actions">
        <div className="row between wrap">
          <span className="t-label" aria-live="polite">
            {formError ? (
              <span className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
                {formError}
              </span>
            ) : Object.keys(errors).length ? (
              'Some fields need fixing.'
            ) : (
              'Starts not live · verify, then go live'
            )}
          </span>
          <button type="submit" className="btn btn-primary btn-lg" disabled={create.isPending} aria-busy={create.isPending || undefined}>
            {create.isPending ? 'Adding…' : 'Add shop'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── the shop record ─────────────────────────────────────────────────────────────────────────

/** /ops/shops/:shopRef: the short name (an old id or retired name is replaced in the bar). */
export function OpsShopDetailPage() {
  const { shopRef = '' } = useParams();
  return (
    <ShopRefGate shopRef={shopRef} prefix="/ops/shops">
      {(ref) => <OpsShopDetail shopId={ref.id} />}
    </ShopRefGate>
  );
}

function OpsShopDetail({ shopId }: { shopId: string }) {
  const q = useQuery({ queryKey: shopKeys.one(shopId), queryFn: () => api.opsShop(shopId), enabled: !!shopId });
  useTitle(q.data?.name ?? 'Shop');
  if (q.isError && !q.data)
    return (
      <>
        <DetailTop title="Shop" />
        <section className="card">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </section>
      </>
    );
  if (!q.data)
    return (
      <>
        <DetailTop title="Shop" />
        <Loading label="Loading the shop" rows={3} />
      </>
    );
  return <ShopRecord key={q.data.id} shop={q.data} />;
}

function BackBtn({ to, label }: { to: string; label: string }) {
  return (
    <Link className="dv-ibtn press mk-back" to={to} aria-label={label} title={label}>
      <Icon name="chevronLeft" />
    </Link>
  );
}

function DetailTop({ title, shop }: { title: string; shop?: OpsShopDetail }) {
  const st = shop ? statusView(shop.status) : null;
  return (
    <header className="dv-head">
      <BackBtn to="/ops/shops" label="Back to shops" />
      <div className="stack s4 grow" style={{ minWidth: 0 }}>
        <h1>{title}</h1>
        {shop && st ? (
          <span className="row wrap mk-headmeta">
            <StDot tone={dotTone(st.tone)}>{st.label}</StDot>
            {needsVerification(shop) ? <NeedsVerification /> : shop.kind === 'partner' ? <StDot tone="ok">Verified</StDot> : null}
            <span className="mk-sub">
              {shop.kind === 'first_party' ? 'First-party' : 'Partner'} · <span className="t-mono">/{shop.slug}</span>
            </span>
          </span>
        ) : null}
      </div>
      {shop && shop.status !== 'pending' ? (
        <div className="dv-actions">
          <Link className="btn btn-quiet" to={shopPath(shop.slug, shop.id)}>
            Open in Partner Hub
          </Link>
        </div>
      ) : null}
    </header>
  );
}

function ShopRecord({ shop }: { shop: OpsShopDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const { draft, setDraft, errors, setErrors, set } = useDraft(draftFromShop(shop));
  const [formError, setFormError] = useState<string | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const key = shopKeys.one(shop.id);

  const saved = (next: OpsShopDetail) => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: opsKeys.shops });
  };

  const check = validateShopDraft(draft, { mode: 'edit' });
  const patch = check.ok ? shopPatch(check.value, shop) : null;
  const dirty = check.ok ? Object.keys(patch ?? {}).length > 0 : true;

  const save = useMutation({
    mutationFn: (p: NonNullable<typeof patch>) => api.updateShop(shop.id, p),
    onSuccess: (next) => {
      saved(next);
      setDraft(draftFromShop(next));
      setErrors({});
      setFormError(null);
      if (next.slug !== shop.slug) {
        // The new short name is the address now; the old one keeps redirecting (docs/SHOP-SHORT-URLS.md).
        qc.removeQueries({ queryKey: shopRefKey(shop.slug) });
        navigate(opsShopPath(next.slug, next.id), { replace: true });
        toast(`${next.name} saved. Its address is now /${next.slug}; the old one still works.`);
      } else toast(`${next.name} saved.`);
    },
    onError: (err) => {
      const s = serverErrors(err, "Couldn't save the shop. Try again.");
      setErrors(s.field);
      setFormError(s.form);
      if (!s.form) focusFirstError(formRef.current);
    },
  });

  const verify = useMutation({
    mutationFn: () => api.verifyShop(shop.id),
    onSuccess: (next) => {
      saved(next);
      toast(`${next.name} is verified. You can put it live now.`);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't verify the shop. Try again."), 'error'),
  });

  const status = useMutation({
    mutationFn: (s: Target) => api.setShopStatus(shop.id, s),
    onSuccess: (_d, s) => {
      setStatusError(null);
      setSuspending(false);
      qc.setQueryData<OpsShopDetail>(key, (old) => (old ? { ...old, status: s } : old));
      void qc.invalidateQueries({ queryKey: ['ops'] });
      toast(`${shop.name} ${s === 'live' ? 'is live' : s === 'paused' ? 'is paused' : 'is suspended'}.`);
    },
    onError: (err) => {
      setSuspending(false);
      setStatusError(statusProblem(err));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const r = validateShopDraft(draft, { mode: 'edit' });
    if (!r.ok) {
      setErrors(r.errors);
      focusFirstError(formRef.current);
      return;
    }
    const p = shopPatch(r.value, shop);
    if (!Object.keys(p).length) return;
    save.mutate(p);
  };

  const ready = verifyReadiness(shop);
  const hint = verifyHint(shop);
  const verified = !needsVerification(shop) && shop.kind === 'partner';
  const actions = statusActions(shop);

  return (
    <>
      <DetailTop title={shop.name} shop={shop} />

      <div className="dv-stats dv-rise" style={{ ['--i' as string]: 0 }}>
        <Stat label="Commission" value={bpsLabel(shop.commissionBps)} sub="of item sales" />
        <Stat label="Prep time" value={`${shop.prepMinutes} min`} sub="usual" />
        <Stat label="Minimum order" value={shop.minOrderCentavos ? peso(shop.minOrderCentavos) : '₱0'} sub={shop.minOrderCentavos ? undefined : 'No minimum'} />
        <Stat label="Owners" value={String(shop.owners.length)} sub={shop.owners.length ? 'in Partner Hub' : 'Invite one'} />
      </div>

      <div className="mk-pair dv-rise" style={{ ['--i' as string]: 1 }}>
        <section className="card dv-card" aria-labelledby="sd-status-h">
          <div className="dv-card-h">
            <h2 id="sd-status-h" title="Live shops show in the app. Paused shops stay listed but take no orders. Suspended shops disappear.">
              Status
            </h2>
            <span className="grow" />
            <StDot tone={dotTone(statusView(shop.status).tone)}>{statusView(shop.status).label}</StDot>
          </div>
          <div className="mk-states" aria-hidden="true">
            {(['pending', 'live', 'paused', 'suspended'] as const).map((k) => (
              <span key={k} className={shop.status === k ? 'on' : ''}>
                <i className={`mk-dot ${dotTone(statusView(k).tone)}`} />
                {statusView(k).label}
              </span>
            ))}
          </div>
          <div className="sh-status-row" role="group" aria-label="Change status">
            {actions.map((a) => (
              <button
                key={a.status}
                type="button"
                className={a.status === 'live' ? 'btn btn-secondary' : a.status === 'suspended' ? 'btn btn-quiet' : 'btn'}
                disabled={a.disabled || status.isPending}
                aria-describedby={a.reason ? 'sd-live-why' : undefined}
                onClick={() => (a.status === 'suspended' ? setSuspending(true) : status.mutate(a.status))}
              >
                {a.label}
              </button>
            ))}
          </div>
          {actions.some((a) => a.reason) ? (
            <p id="sd-live-why" className="note row mk-note" role="note">
              <Icon name="alert" />
              <span>{actions.find((a) => a.reason)?.reason}</span>
            </p>
          ) : null}
          {statusError ? (
            <span className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {statusError}
            </span>
          ) : null}
        </section>

        <section className="card dv-card" aria-labelledby="sd-verify-h">
          <div className="dv-card-h">
            <h2
              id="sd-verify-h"
              title={shop.kind === 'first_party' ? 'First-party shops are PopCart’s own. No vetting needed.' : 'Verify once you’ve checked the seller’s papers and pickup spot. We record who verified and when.'}
            >
              Verification
            </h2>
            <span className="grow" />
            {shop.kind === 'first_party' ? <span className="dv-chip">Not needed</span> : verified ? <span className="dv-chip good">Verified</span> : <span className="dv-chip bad">{ready.missing.length ? `${ready.missing.length} missing` : 'Ready'}</span>}
          </div>
          <dl className="mk-facts">
            {[
              ['Legal name', shop.legalName],
              ['DTI/SEC no.', shop.registrationNo],
              ['TIN', shop.tin],
              ['Pin', shop.location ? latLngLabel(shop.location) : null],
            ].map(([k, v]) => (
              <div key={k as string} className={v ? 'has' : ''}>
                <dt>
                  <i className={`mk-dot ${v ? 'ok' : 'off'}`} aria-hidden="true" />
                  {k}
                </dt>
                <dd className={k === 'Pin' ? 't-mono' : undefined}>{v || <span className="muted">Not set</span>}</dd>
              </div>
            ))}
          </dl>
          {shop.kind === 'partner' ? (
            verified ? (
              <p className="row mk-ok">
                <Icon name="check" />
                Verified {shop.verifiedAt ? stampDay(shop.verifiedAt) : ''}
                {shop.verifiedBy?.name ? ` by ${shop.verifiedBy.name}` : ''}.
              </p>
            ) : (
              <div className="row wrap">
                <button type="button" className="btn btn-secondary" disabled={!ready.ready || verify.isPending} aria-describedby={hint ? 'sd-verify-why' : undefined} aria-busy={verify.isPending || undefined} onClick={() => verify.mutate()}>
                  <Icon name="check" />
                  {verify.isPending ? 'Verifying…' : 'Verify shop'}
                </button>
                {hint ? (
                  <span id="sd-verify-why" className="mk-sub grow">
                    {hint}
                  </span>
                ) : null}
              </div>
            )
          ) : null}
        </section>
      </div>

      
<Owners shop={shop} onSaved={saved} />

      <form ref={formRef} className="stack s24" onSubmit={submit} noValidate aria-label="Edit shop">
        <ShopForm draft={draft} errors={errors} set={set} mode="edit" />
        <div className="card panel sh-actions">
          <div className="row between wrap">
            <span className="t-label" aria-live="polite">
              {formError ? (
                <span className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
                  {formError}
                </span>
              ) : !check.ok ? (
                'Some fields need fixing before saving.'
              ) : dirty ? (
                'Unsaved changes'
              ) : (
                'All changes saved'
              )}
            </span>
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={save.isPending}
                onClick={() => {
                  setDraft(draftFromShop(shop));
                  setErrors({});
                  setFormError(null);
                }}
              >
                Discard changes
              </button>
              <button type="submit" className="btn btn-primary btn-lg" disabled={!dirty || save.isPending} aria-busy={save.isPending || undefined}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={suspending}
        title={`Suspend ${shop.name}?`}
        body="Customers stop seeing it right away and open orders stay with the shop. You can reinstate it later."
        confirmLabel="Suspend shop"
        busy={status.isPending}
        onConfirm={() => status.mutate('suspended')}
        onClose={() => setSuspending(false)}
      />
    </>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function stampDay(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `on ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function Owners({ shop, onSaved }: { shop: OpsShopDetail; onSaved(s: OpsShopDetail): void }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (e: string) => api.inviteShopOwner(shop.id, e),
    onSuccess: (next, e) => {
      if (next && typeof next === 'object' && 'owners' in next) onSaved(next);
      toast(`We emailed ${e} a link to the Partner Hub.`);
      setEmail('');
      setErr(null);
    },
    onError: (e) => setErr(errorMessage(e, "Couldn't send the invite. Try again.")),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = normalizeEmail(email);
    if (!v) return setErr('Enter their email.');
    if (!emailOk(v)) return setErr('That email doesn’t look right. Check it and try again.');
    if (shop.owners.some((o) => normalizeEmail(o.email) === v)) return setErr('They already own this shop.');
    setErr(null);
    invite.mutate(v);
  };
  return (
    <section className="card dv-card dv-rise" aria-labelledby="sd-owners-h" style={{ ["--i" as string]: 2 }}>
      <div className="dv-card-h">
        <h2 id="sd-owners-h" title="Owners run the shop in the Partner Hub and can add their own staff.">
          Owners
        </h2>
        <span className="dv-chip">{shop.owners.length}</span>
      </div>
      {shop.owners.length ? (
        <ul className="sh-owners" aria-label="Owners">
          {shop.owners.map((o) => (
            <li key={o.userId}>
              <span className="avatar" aria-hidden="true">
                {initials(o.name, o.email)}
              </span>
              <span className="stack tight grow">
                <b className="t-label">{o.name || o.email || 'Invited owner'}</b>
                {o.email && o.name ? <span className="t-caption muted">{o.email}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="dv-quiet">No owner yet</div>
      )}
      <form className="row wrap" style={{ alignItems: 'flex-end' }} onSubmit={submit} noValidate aria-label="Invite another owner">
        <div className="field grow">
          <label htmlFor="sd-owner-email">Invite another owner</label>
          <input
            id="sd-owner-email"
            className="input"
            type="email"
            inputMode="email"
            autoComplete="off"
            maxLength={254}
            value={email}
            aria-invalid={err ? true : undefined}
            aria-describedby={err ? 'sd-owner-err' : undefined}
            onChange={(e) => {
              setEmail(e.target.value);
              if (err) setErr(null);
            }}
          />
        </div>
        <button type="submit" className="btn" disabled={invite.isPending} aria-busy={invite.isPending || undefined}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </form>
      {err ? (
        <span id="sd-owner-err" className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
          {err}
        </span>
      ) : null}
    </section>
  );
}
